import gql from "graphql-tag"
import React from "react"

import TruncatedText from "#Components/shared/TruncatedText"
import { useGraphqlQuery } from "#React/hooks/useGraphqlQuery"
import { dashboardLinks } from "#React/router/links"
import tanstackRouter from "#React/router/routeTree"
import { useCurrentParams } from "#React/router/typedRouteHooks"
import { Button } from "#React/ui"
import {
  type ComboboxChangeEventDetails,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxSearch,
  ComboboxTrigger,
} from "#React/ui/Combobox"
import EnvironmentLabel from "#React/ui/EnvironmentLabel"
import Icon from "#React/ui/Icon"
import LoadingSpinner from "#React/ui/LoadingSpinner/LoadingSpinner"

export const APP_SWITCHER_ORGANIZATION_QUERY = gql`
  query AppSwitcherOrganizationQuery($slug: String!) {
    organization(slug: $slug) {
      id
      name
      slug
      apps {
        id
        name
        environment
        viewerPinned
      }
    }
  }
`

interface App {
  id: string
  name: string
  environment: string
  viewerPinned: boolean
}

interface Organization {
  id: string
  name: string
  slug: string
  apps: App[]
}

interface OrganizationQueryData {
  organization: Organization
}

interface TriggerLabelProps {
  loading: boolean
  organization?: Organization
  selectedApp?: App
}

const TriggerLabel = ({ loading, organization, selectedApp }: TriggerLabelProps) => {
  if (loading) {
    return <LoadingSpinner spinnerClassName="[&+span]:text-current text-current" />
  }

  if (selectedApp) {
    return (
      <>
        <TruncatedText
          text={selectedApp.name}
          textClassName="text-current lg:max-w-20 xl:max-w-40 inline-block"
        />
        <span className="ml-2">
          <EnvironmentLabel env={selectedApp.environment} className="font-normal" />
        </span>
      </>
    )
  }

  return (
    <TruncatedText
      text={organization?.name || ""}
      textClassName="text-current lg:max-w-20 xl:max-w-40 inline-block"
    />
  )
}

const AppSwitcher = () => {
  const { organizationSlug, appId } = useCurrentParams()

  const { isLoading, data } = useGraphqlQuery<OrganizationQueryData, { slug: string }>(APP_SWITCHER_ORGANIZATION_QUERY, {
    variables: { slug: organizationSlug ?? "" },
    enabled: !!organizationSlug,
  })
  const loading = isLoading && !!organizationSlug

  const organization = data?.organization
  const apps = organization?.apps

  const selectedApp = React.useMemo(() => apps?.find((app) => app.id === appId), [apps, appId])

  const sortedApps = React.useMemo(
    () =>
      [...(apps ?? [])].sort((a, b) => {
        if (a.viewerPinned !== b.viewerPinned) return a.viewerPinned ? -1 : 1
        return a.name.localeCompare(b.name)
      }),
    [apps],
  )

  const hasApps = (apps?.length ?? 0) > 0

  const getAppPath = (app: App) => {
    return tanstackRouter.buildLocation(
      dashboardLinks.index({ appId: app.id, organizationSlug: organizationSlug! }),
    ).href
  }

  const opensInNewTab = (event: MouseEvent | KeyboardEvent | PointerEvent | undefined) => {
    if (!event) {
      return false
    }

    return event.metaKey || event.ctrlKey
  }

  // BaseUI, only fires onChange for primary buttons. That's correct.
  // To handle middle mouse clicks, let's capture it separately. It doesn't affect the state
  // of the combobox, so it should be a separate side-effect.
  const handleAuxClick = (app: App, event: React.MouseEvent) => {
    if (event.button !== 1) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    window.open(getAppPath(app), "_blank", "noopener")
  }

  const handleValueChange = (app: App | null, eventDetails: ComboboxChangeEventDetails) => {
    if (!app || !organizationSlug) return

    const event = eventDetails.event as MouseEvent | KeyboardEvent | PointerEvent | undefined
    if (opensInNewTab(event)) {
      window.open(getAppPath(app), "_blank", "noopener")
      return
    }

    window.location.href = getAppPath(app)
  }

  const renderTrigger = ({
    color: _color,
    onClick,
    ...triggerProps
  }: React.ComponentProps<"button">) => (
    <Button
      {...triggerProps}
      onClick={onClick as React.ComponentProps<typeof Button>["onClick"]}
      color="white"
      className="border-0 font-semibold lg:bg-gray-900 lg:dark:bg-surface-default lg:hover:bg-gray-900 lg:dark:hover:bg-surface-default [&_i]:text-gray-300 lg:text-white max-lg:shadow-none max-lg:w-full max-lg:[&>span]:w-full max-lg:[&>span]:gap-0 max-lg:rounded-none max-lg:h-9 max-lg:px-5 max-lg:border-b max-lg:border-edge-default"
    >
      <TriggerLabel loading={loading} organization={organization} selectedApp={selectedApp} />
      <span className="ml-auto">
        <Icon variant="disclosure" tone="muted" />
      </span>
    </Button>
  )

  return (
    <ComboboxRoot<App>
      items={sortedApps}
      itemToStringLabel={(app) => app.name}
      itemToStringValue={(app) => app.id}
      onValueChange={handleValueChange}
      value={selectedApp ?? null}
      disabled={!hasApps}
      autoHighlight
    >
      <ComboboxTrigger render={renderTrigger} />
      <ComboboxContent className="max-xs:w-(--available-width) lg:w-80">
        <ComboboxSearch placeholder="Search application" />
        <ComboboxEmpty>No applications found.</ComboboxEmpty>
        <ComboboxList>
          {(app: App) => (
            <ComboboxItem
              key={app.id}
              value={app}
              showIndicator={false}
              className="justify-between data-selected:bg-surface-raised"
              onAuxClick={(event) => handleAuxClick(app, event)}
            >
              {app.viewerPinned && (
                <Icon name="fas fa-star" size="xs" className="text-yellow-500 shrink-0" />
              )}
              <TruncatedText text={app.name} textClassName="flex-1 min-w-0" />
              <EnvironmentLabel env={app.environment} />
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  )
}

export default AppSwitcher
